import {
  Center,
  useDisclosure,
  Modal,
  ModalContent,
  ModalBody,
  Button,
  ModalHeader,
  ModalFooter,
  ButtonGroup,
  IconButton,
  ModalOverlay,
  useBoolean,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  useMediaQuery,
} from '@chakra-ui/react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Api } from 'api';
import { DataTable, LoadingIndicator } from 'components';
import { useMutation, useQuery } from 'react-query';
import { ExpertisePackageAbilityDefinitionType, ExpertisePackageWithAbilities, ExpertiseQueryCacheKey } from 'types';
import React, { useMemo, useState } from 'react';
import { AddIcon, CheckIcon, CloseIcon, DeleteIcon, EditIcon, SearchIcon } from '@chakra-ui/icons';
import { t, Trans } from '@lingui/macro';
import { AdminPackageAbilitiesForm, AdminPageTitle } from '../components';

type ExpertisePackagesWithAbilitiesDatatableType = {
  id: number;
  name: string;
  price: number;
  paint: boolean;
  micron: boolean;
  sasi: boolean;
  body: boolean;
  mechanic: boolean;
  km: boolean;
  damage: boolean;
  reportEkspertise: boolean;
  reportBreakdown: boolean;
  reportKm: boolean;
  reportConta: boolean;
  multiplePhoto: boolean;
  roadAssistance: boolean;
  secondHandGuarantee: boolean;
  isActive: boolean;
};
export const AdminPackageManagementPage = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  const {
    isOpen: isDeleteConfirmDialogOpen,
    onOpen: onDeleteConfirmDialogOpen,
    onClose: onDeleteConfirmDialogClose,
  } = useDisclosure();
  const deleteDialogCancelRef = React.useRef(null);

  const [focusedPackage, setFocusedPackage] = useState<ExpertisePackageWithAbilities>();
  const [searchQuery, setSearchQuery] = useState('');
  const query = useQuery(
    ExpertiseQueryCacheKey.GetExpertisePackagesWithAbilities,
    async () => {
      const response = await Api.Expertise.getExpertisePackagesWithAbilities();
      const packages = response.data;
      const datatable = packages.map((expertisePackage) => {
        const abilittySet = new Set(expertisePackage.abilities.map((ability) => ability.abilityDefinitionTypeId));
        return {
          id: expertisePackage.expertisePackage.id,
          name: expertisePackage.expertisePackage.name,
          price: parseInt(expertisePackage.expertisePackage.price, 10),
          paint: abilittySet.has(ExpertisePackageAbilityDefinitionType.BoyaDegisenFormu),
          micron: abilittySet.has(ExpertisePackageAbilityDefinitionType.MikronKaydiFormu),
          sasi: abilittySet.has(ExpertisePackageAbilityDefinitionType.SasiKontrol),
          body: abilittySet.has(ExpertisePackageAbilityDefinitionType.KaportaKontrolNotlari),
          mechanic: abilittySet.has(ExpertisePackageAbilityDefinitionType.MotorMekanikKontrolNotlari),
          km: abilittySet.has(ExpertisePackageAbilityDefinitionType.KMKaydiSorgulama),
          damage: abilittySet.has(ExpertisePackageAbilityDefinitionType.HasarKaydiSorgulama),
          reportEkspertise: abilittySet.has(ExpertisePackageAbilityDefinitionType.EkspertizCihaziTestSonuclari),
          reportBreakdown: abilittySet.has(ExpertisePackageAbilityDefinitionType.ArizaTespitSonuclari),
          reportKm: abilittySet.has(ExpertisePackageAbilityDefinitionType.KMTespitSonuclari),
          reportConta: abilittySet.has(ExpertisePackageAbilityDefinitionType.ContaKacakTespitSonuclari),
          multiplePhoto: abilittySet.has(ExpertisePackageAbilityDefinitionType.AracFotografiCoklu),
          roadAssistance: abilittySet.has(ExpertisePackageAbilityDefinitionType.YolYardimSertifikasi),
          secondHandGuarantee: abilittySet.has(ExpertisePackageAbilityDefinitionType.AracKorumaSertifikasi),
          isActive: expertisePackage.expertisePackage.active,
        };
      }) as ExpertisePackagesWithAbilitiesDatatableType[];
      return {
        datatable,
        packagesWithAbilities: response.data,
      };
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );

  const filteredData = useMemo(() => {
    const { data } = query;
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      return {
        packagesWithAbilities: data?.packagesWithAbilities,
        datatable: data?.datatable.filter((item) => item.name.toLowerCase().includes(searchLower)),
      };
    }
    return data;
  }, [query.data, searchQuery]);

  const deleteMutation = useMutation<void, void, number>(
    async (packageId: number) => {
      await Api.Expertise.deletePackage(packageId);
    },
    {
      onSuccess: () => {
        query.refetch();
      },
    },
  );

  const columnHelper = createColumnHelper<ExpertisePackagesWithAbilitiesDatatableType>();

  const columnHeaderBaseMeta = {
    style: {
      textAlign: 'center',
    } as React.CSSProperties,
  } as any;
  const columnDef: ColumnDef<ExpertisePackagesWithAbilitiesDatatableType>[] = [
    {
      header: ' ',
      columns: [
        columnHelper.accessor('name', {
          header: 'Paket',
        }),
      ],
    },
    {
      header: t`Formlar`,
      meta: columnHeaderBaseMeta,
      columns: [
        columnHelper.accessor('paint', {
          header: t`Boya`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
        columnHelper.accessor('micron', {
          header: t`Mikron`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
        columnHelper.accessor('sasi', {
          header: t`Şasi`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
      ],
    },
    {
      header: 'Notlar',
      meta: columnHeaderBaseMeta,
      columns: [
        columnHelper.accessor('body', {
          header: t`Kaporta`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
        columnHelper.accessor('mechanic', {
          header: t`Mekanik`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
      ],
    },
    {
      header: 'Sorgu',
      meta: columnHeaderBaseMeta,
      columns: [
        columnHelper.accessor('km', {
          header: t`KM`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
        columnHelper.accessor('damage', {
          header: t`Hasar`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
      ],
    },
    {
      header: 'Sonuçlar',
      meta: columnHeaderBaseMeta,
      columns: [
        columnHelper.accessor('reportEkspertise', {
          header: t`Ekspertiz`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
        columnHelper.accessor('reportBreakdown', {
          header: t`Arıza`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
        columnHelper.accessor('reportKm', {
          header: t`KM`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
        columnHelper.accessor('reportConta', {
          header: t`Conta`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
      ],
    },
    {
      header: t`Sertifikalar`,
      meta: columnHeaderBaseMeta,
      columns: [
        columnHelper.accessor('roadAssistance', {
          header: t`Yol Yardım`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
        columnHelper.accessor('secondHandGuarantee', {
          header: t`İkinci El Garanti`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
      ],
    },
    {
      header: t`Fotoğraf`,
      meta: columnHeaderBaseMeta,
      columns: [
        columnHelper.accessor('multiplePhoto', {
          header: t`Çoklu`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
      ],
    },
    {
      header: ' ',
      columns: [
        columnHelper.accessor('price', {
          header: t`Fiyat`,
          meta: { ...columnHeaderBaseMeta, isNumeric: true },
          cell: (info) => {
            return `₺${info.getValue()}`;
          },
        }),
        columnHelper.accessor('isActive', {
          header: t`Aktif Mi?`,
          meta: columnHeaderBaseMeta,
          cell: (info) => {
            return <Center>{info.getValue() ? <CheckIcon color="green" /> : <CloseIcon color="red" />}</Center>;
          },
        }),
      ],
    },
    {
      header: t`Aksiyonlar`,
      cell: (cellProps) => {
        const { row } = cellProps;
        return (
          <ButtonGroup gap={2}>
            <IconButton
              icon={<EditIcon />}
              aria-label={t`Düzenle`}
              colorScheme="blue"
              onClick={() => {
                if (query.data) {
                  const elem = query.data.packagesWithAbilities.find(
                    (pkg) => pkg.expertisePackage.id === row.original.id,
                  );
                  if (elem) {
                    setFocusedPackage(elem);
                    onEditModalOpen();
                  }
                }
              }}
            />
            <IconButton
              icon={<DeleteIcon />}
              aria-label={t`Sil`}
              colorScheme="red"
              onClick={() => {
                if (query.data) {
                  const elem = query.data.packagesWithAbilities.find(
                    (pkg) => pkg.expertisePackage.id === row.original.id,
                  );
                  if (elem) {
                    setFocusedPackage(elem);
                    onDeleteConfirmDialogOpen();
                  }
                }
              }}
            />
          </ButtonGroup>
        );
      },
    },
  ];

  const abilitiesQuery = useQuery(
    ExpertiseQueryCacheKey.GetAllAbilities,
    async () => {
      const response = await Api.Expertise.getAllAbilities();
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const [isLoading, { on: startLoading, off: stopLoading }] = useBoolean();

  return (
    <>
      {abilitiesQuery.data && (
        <>
          <Modal isOpen={isCreateModalOpen} onClose={onCreateModalClose}>
            <ModalOverlay />

            <ModalContent>
              <ModalHeader>
                <Trans>Paket Oluştur</Trans>
              </ModalHeader>

              <ModalBody>
                <AdminPackageAbilitiesForm
                  abilities={abilitiesQuery.data}
                  formId="createPackageForm"
                  onSubmitComplete={(isSuccess: boolean) => {
                    if (isSuccess) {
                      onCreateModalClose();
                      query.refetch();
                    }
                    stopLoading();
                  }}
                  onSubmitStart={startLoading}
                />
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button onClick={onCreateModalClose} disabled={isLoading}>
                    <Trans>İptal</Trans>
                  </Button>
                  <Button colorScheme="green" type="submit" form="createPackageForm" isLoading={isLoading}>
                    <Trans>Kaydet</Trans>
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>
          <Modal isOpen={isEditModalOpen} onClose={onEditModalClose}>
            <ModalOverlay />

            <ModalContent>
              <ModalHeader>
                <Trans>Paket Düzenle</Trans>
              </ModalHeader>

              <ModalBody>
                <AdminPackageAbilitiesForm
                  packageWithAbilities={focusedPackage}
                  abilities={abilitiesQuery.data}
                  formId="editPackageForm"
                  onSubmitComplete={(isSuccess: boolean) => {
                    if (isSuccess) {
                      onEditModalClose();
                      query.refetch();
                    }
                    stopLoading();
                  }}
                  onSubmitStart={startLoading}
                />
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button onClick={onEditModalClose} disabled={isLoading}>
                    <Trans>İptal</Trans>
                  </Button>
                  <Button colorScheme="green" type="submit" form="editPackageForm" isLoading={isLoading}>
                    <Trans>Kaydet</Trans>
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>
          <AlertDialog
            isOpen={isDeleteConfirmDialogOpen}
            onClose={onDeleteConfirmDialogClose}
            leastDestructiveRef={deleteDialogCancelRef}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  <Trans>Onay</Trans>
                </AlertDialogHeader>

                <AlertDialogBody>
                  <Trans>Silmek istediğinize emin misiniz?</Trans>
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button
                    onClick={onDeleteConfirmDialogClose}
                    ref={deleteDialogCancelRef}
                    disabled={deleteMutation.isLoading}
                  >
                    <Trans>İptal</Trans>
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={() => {
                      if (focusedPackage) {
                        deleteMutation.mutate(focusedPackage.expertisePackage.id);
                      }
                      onDeleteConfirmDialogClose();
                    }}
                    ml={3}
                    isLoading={deleteMutation.isLoading}
                  >
                    <Trans>Sil</Trans>
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      )}

      <AdminPageTitle title={t`Paket Yönetimi`} />
      {query.isLoading ? (
        <LoadingIndicator />
      ) : (
        <DataTable
          data={filteredData?.datatable || []}
          columns={columnDef}
          usePagination
          actions={
            <HStack spacing={4} justifyContent="space-between" w="100%">
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder={t`Paket ara...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Button colorScheme="green" onClick={onCreateModalOpen} leftIcon={!isMobile ? <AddIcon /> : undefined}>
                <Trans>Ekle</Trans>
              </Button>
            </HStack>
          }
        />
      )}
    </>
  );
};
